// TikTok Pixel event helpers — thin, defensive wrappers around the global
// `ttq` object the base Pixel snippet (index.html) loads.
//
// Why wrap instead of calling `window.ttq.track(...)` directly in every
// component:
//   1. Every call here is guarded. If the snippet hasn't finished loading
//      yet, is blocked by an ad blocker, or a consent tool hasn't granted
//      analytics consent, these silently no-op instead of throwing —
//      tracking should never be able to break the click, submit, or
//      navigation it's attached to.
//   2. One file to read to see every event this site fires and its exact
//      payload shape, instead of hunting through components.
//
// Official event reference:
// https://ads.tiktok.com/marketing_api/docs?id=1739584700709634

function ttqCall(method, ...args) {
  if (typeof window === 'undefined' || !window.ttq) return
  try {
    window.ttq[method](...args)
  } catch {
    // Never let a tracking failure break the feature it's attached to.
  }
}

/**
 * Fires when a visitor opens a project/case study (Transformations.jsx,
 * when the case-study overlay opens).
 */
export function trackViewContent({ id, name } = {}) {
  ttqCall('track', 'ViewContent', {
    content_id: id,
    content_name: name,
    content_type: 'product',
  })
}

/**
 * Fires once per genuinely successful inquiry-form submission.
 * Deliberately NOT called from the honeypot's faked-success branch in
 * InquiryForm.jsx — that path exists to quietly no-op on bots, and firing
 * a Contact conversion for bot traffic would feed TikTok's optimization
 * bad signal instead of just wasting a form row.
 */
export function trackContact({ tier, growthPlan } = {}) {
  ttqCall('track', 'Contact', {
    content_name: tier || 'unspecified',
    ...(growthPlan && { content_category: growthPlan }),
  })
}

/**
 * Fires when a primary conversion CTA is clicked — see Button.jsx's
 * `ttqTrack` prop, which is set explicitly on the specific CTA instances
 * that should count (Hero, Navbar, FinalCTA, CampaignHero, InsightArticle).
 * Deliberately opt-in per button rather than automatic for every
 * variant="primary" button: the inquiry form's own submit button is also
 * primary-styled, and that click already fires Contact on success —
 * tracking it as a ClickButton too would double-count the same action
 * under two different events.
 */
export function trackClickButton({ label, href } = {}) {
  ttqCall('track', 'ClickButton', {
    content_name: label,
    ...(href && { content_id: href }),
  })
}
